import { SelectableTableColumn } from '../SelectableTable/SelectableTable.types';
import { VerticalTabs } from '../Tabs/VerticalTabs/VerticalTabs.component';
import { TabItem } from '../Tabs/VerticalTabs/VerticalTabs.types';
import {
  Adjustment,
  Adjustments,
  AdjustmentTableItem,
  AdjustmentType,
  ImportAdjustmentsStructure,
} from '../../models/adjustment.types';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { AdjustmentsTable } from './AdjustmentsTable.component';
import { ModalWithButtons } from '../ModalWithButtons/ModalWithButtons.component';
import { useTranslation } from 'react-i18next';
import {
  getAdjustments,
  importAdjustments,
  importAdjustmentsFromSioeo,
  updateAdjustments,
} from '../../services/exams/examSheet/examSheet.service';
import { useLayoutContext } from '../../Context/Layout.context';
import { LeavePrompt } from '../Prompt/LeavePrompt/LeavePrompt.component';
import { AxiosResponse } from 'axios';
import SwitchOne from '../SwitchOne/SwitchOne.component';

interface AdjustmentsModalProps {
  examSheetId: string;
  title: string;
  isConfigured?: boolean;
  onClose(): void;
  importedFromExternalSystem?: boolean;
  isKK?: boolean;
  selectedSystem?: string;
}

export const inputTableBorder = '1px solid rgba(224, 224, 224, 1)';

export const AdjustmentsModal: React.FC<AdjustmentsModalProps> = (props) => {
  const { openAlert, setIsLoader } = useLayoutContext();
  const [leavePromptOpen, setLeavePromptOpen] = useState<boolean>(false);
  const [adjustmentTypesTableItems, setAdjustmentypesTableItems] = useState<AdjustmentTableItem[]>([]);
  const [adjustmentPermissionsTableItems, setAdjustmentPermissionsTableItems] = useState<AdjustmentTableItem[]>([]);
  const [parsedAdjustmentsStructure, setParsedAdjustmentStructure] = useState<ImportAdjustmentsStructure>();
  const [importMode, setImportMode] = useState<AdjustmentType>();
  const [saveButtonDisabled, setSaveButtonDisabled] = useState<boolean>(true);
  const [columns, setColumns] = useState<SelectableTableColumn[]>([]);

  useEffect(() => {
    const adjustmentColumns: SelectableTableColumn[] = [
      {
        id: 'id',
        type: 'text',
        title: 'ID',
        filterType: null,
      },
      {
        id: 'name',
        type: 'text',
        title: 'Nazwa',
        filterType: null,
      },
      {
        id: 'isActiveElement',
        type: 'children',
        title: 'Dostosowane kryteria oceniania',
        filterType: null,
        style: { borderRight: inputTableBorder },
      },
    ];
    if (props.selectedSystem === 'SIOEO') {
      adjustmentColumns.push({
        id: 'sioeoImportElement',
        type: 'children',
        title: 'Import zdających z SIOEO',
        filterType: null,
      });
    }
    setColumns(adjustmentColumns);
  }, [props.selectedSystem]);

  const changedEvalutationCriteriaState = (
    checked: boolean,
    id: string,
    setStateFunc: Dispatch<SetStateAction<AdjustmentTableItem[]>>,
  ) => {
    setStateFunc((existingAdjustments) => {
      return existingAdjustments.map((adjustment: AdjustmentTableItem) => {
        return adjustment.id === id
          ? {
              ...adjustment,
              isActive: checked,
              isActiveElement: (
                <SwitchOne
                  disableRipple
                  color={'primary'}
                  checked={checked}
                  onChange={(event, checked: boolean) =>
                    changedEvalutationCriteriaState(checked, adjustment.id, setStateFunc)
                  }
                />
              ),
              adjustmentModified: !adjustment.adjustmentModified,
            }
          : adjustment;
      });
    });
  };

  const changedImportSIOEOState = (
    checked: boolean,
    id: string,
    setStateFunc: Dispatch<SetStateAction<AdjustmentTableItem[]>>,
  ) => {
    setStateFunc((existingAdjustments) => {
      return existingAdjustments.map((adjustment: AdjustmentTableItem) => {
        return adjustment.id === id
          ? {
              ...adjustment,
              sioeoImport: checked,
              sioeoImportElement: (
                <SwitchOne
                  disableRipple
                  color={'primary'}
                  checked={checked}
                  onChange={(event, checked: boolean) => changedImportSIOEOState(checked, adjustment.id, setStateFunc)}
                />
              ),
              sioeoImportModified: !adjustment.sioeoImportModified,
            }
          : adjustment;
      });
    });
  };

  const buildTableItems = (data: Adjustment[], setStateFunc: Dispatch<SetStateAction<AdjustmentTableItem[]>>) => {
    const tableItems: AdjustmentTableItem[] = [];
    data.forEach((adjustment: Adjustment) => {
      tableItems.push({
        id: adjustment.id,
        oneId: adjustment.oneId,
        name: adjustment.name,
        isActive: adjustment.isActive,
        sioeoImport: adjustment.sioeoImport,
        isActiveElement: (
          <SwitchOne
            disableRipple
            color={'primary'}
            checked={adjustment.isActive}
            onChange={(event, checked: boolean) =>
              changedEvalutationCriteriaState(checked, adjustment.id, setStateFunc)
            }
            inputProps={{ 'aria-label': 'Przełącznik aktywności dostosowanych kryteriów oceniania' }}
          />
        ),
        sioeoImportElement: (
          <SwitchOne
            disableRipple
            color={'primary'}
            checked={adjustment.sioeoImport}
            onChange={(event, checked: boolean) => changedImportSIOEOState(checked, adjustment.id, setStateFunc)}
            inputProps={{ 'aria-label': 'przełącznik aktywności importu zdających z SIOEO' }}
          />
        ),
      });
    });
    setStateFunc(tableItems);
  };

  const getAdjustmentsDataAndBuildTableItems = () => {
    setIsLoader(true);
    getAdjustments(props.examSheetId)
      .then(
        (result: Adjustments) => {
          buildTableItems(result.adjustmentTypes, setAdjustmentypesTableItems);
          buildTableItems(result.adjustmentPermissions, setAdjustmentPermissionsTableItems);
        },
        () => {
          openAlert('error');
        },
      )
      .finally(() => setIsLoader(false));
  };

  useEffect(() => {
    getAdjustmentsDataAndBuildTableItems();
  }, []);

  useEffect(() => {
    if (importMode !== undefined && parsedAdjustmentsStructure !== undefined) {
      setSaveButtonDisabled(false);
    } else if (
      adjustmentTypesTableItems.find((item) => item.adjustmentModified) ||
      adjustmentTypesTableItems.find((item) => item.sioeoImportModified) ||
      adjustmentPermissionsTableItems.find((item) => item.adjustmentModified) ||
      adjustmentPermissionsTableItems.find((item) => item.sioeoImportModified)
    ) {
      setSaveButtonDisabled(false);
    } else setSaveButtonDisabled(true);
  }, [importMode, adjustmentTypesTableItems, adjustmentPermissionsTableItems, parsedAdjustmentsStructure]);

  const handleImportFromExternalSystem = () => {
    setIsLoader(true);
    importAdjustmentsFromSioeo(props.examSheetId)
      .then(
        (result) => {
          if (result.data === true) {
            openAlert('success');
            setImportMode(undefined);
            setParsedAdjustmentStructure(undefined);
            getAdjustmentsDataAndBuildTableItems();
          } else {
            openAlert('error');
          }
        },
        () => {
          openAlert('error');
        },
      )
      .finally(() => setIsLoader(false));
  };

  const items: TabItem[] = [
    {
      label: 'Sposoby dostosowań',
      children: (
        <AdjustmentsTable
          type={AdjustmentType.means}
          items={adjustmentTypesTableItems}
          columns={columns}
          onImportClick={() => setImportMode(AdjustmentType.means)}
          closeImport={importMode === undefined}
          isConfigured={props.isConfigured}
          onValidFileSelected={(parsedStructure) => setParsedAdjustmentStructure(parsedStructure)}
          importedFromExternalSystem={
            props.importedFromExternalSystem !== undefined ? props.importedFromExternalSystem : false
          }
          onImportFromExternalSystem={handleImportFromExternalSystem}
          selectedSystem={props.selectedSystem}
        />
      ),
    },
    {
      label: 'Uprawnienia do dostosowań',
      children: (
        <AdjustmentsTable
          type={AdjustmentType.permissions}
          items={adjustmentPermissionsTableItems}
          columns={columns}
          onImportClick={() => setImportMode(AdjustmentType.permissions)}
          closeImport={importMode === undefined}
          isConfigured={props.isConfigured}
          onValidFileSelected={(parsedStructure) => setParsedAdjustmentStructure(parsedStructure)}
          importedFromExternalSystem={
            props.importedFromExternalSystem !== undefined ? props.importedFromExternalSystem : false
          }
          onImportFromExternalSystem={handleImportFromExternalSystem}
          selectedSystem={props.selectedSystem}
        />
      ),
    },
  ];

  const getModalTitle = () => {
    return `Dostosowania: ${props.title}`;
  };

  const onLeaveConfirm = () => {
    if (importMode !== undefined) {
      setImportMode(undefined);
      setParsedAdjustmentStructure(undefined);
      setLeavePromptOpen(false);
    } else {
      props.onClose();
    }
  };

  const getAllModifiedAdjustments = () => {
    const adjustments: Adjustment[] = [];
    const adjustmentsPermissions: Adjustment[] = [];

    adjustmentTypesTableItems.forEach((item) => {
      adjustments.push({
        id: item.oneId!,
        isActive: item.isActive,
        sioeoImport: item.sioeoImport,
      });
    });

    adjustmentPermissionsTableItems.forEach((item) => {
      adjustmentsPermissions.push({
        id: item.oneId!,
        isActive: item.isActive,
        sioeoImport: item.sioeoImport,
      });
    });
    return {
      adjustments: adjustments,
      adjustmentsPermissions: adjustmentsPermissions,
    };
  };

  const handleConfirm = () => {
    setIsLoader(true);
    if (importMode !== undefined && parsedAdjustmentsStructure !== undefined) {
      importAdjustments(props.examSheetId, parsedAdjustmentsStructure)
        .then(
          (result: AxiosResponse<boolean>) => {
            if (result.data === true) {
              openAlert('success');
              setImportMode(undefined);
              setParsedAdjustmentStructure(undefined);
              getAdjustmentsDataAndBuildTableItems();
            } else {
              openAlert('error');
              setSaveButtonDisabled(true);
            }
          },
          () => {
            openAlert('error');
          },
        )
        .finally(() => setIsLoader(false));
    } else {
      updateAdjustments(props.examSheetId, getAllModifiedAdjustments(), !!props.isKK)
        .then(
          (result: AxiosResponse<boolean>) => {
            if (result.data === true) {
              openAlert('success');
              getAdjustmentsDataAndBuildTableItems();
            } else {
              openAlert('error');
            }
          },
          () => {
            openAlert('error');
          },
        )
        .finally(() => setIsLoader(false));
    }
  };

  return (
    <ModalWithButtons
      open
      xCloseButton={props.onClose}
      title={getModalTitle()}
      size={'lg'}
      autoHeight
      confirmButtonDisabled={saveButtonDisabled}
      onConfirm={handleConfirm}
      onCancel={() => {
        if (importMode !== undefined && saveButtonDisabled) {
          setImportMode(undefined);
          props.onClose();
        } else if (!saveButtonDisabled) {
          setLeavePromptOpen(true);
        } else {
          props.onClose();
        }
      }}
    >
      <VerticalTabs items={items} />
      <LeavePrompt isOpen={leavePromptOpen} onCancel={() => setLeavePromptOpen(false)} onConfirm={onLeaveConfirm} />
    </ModalWithButtons>
  );
};
